import { useReducer, useCallback, useEffect } from 'react';
import { getAllInstructorsApi, getAllMainCategoriesApi, getReactionApi } from '@/api/adminApis/adminApis';
import Toast from '@/utils/toastManager';

const DEFAULT_SORT = 'newest';
const DEFAULT_FILTER_STATE = {
	searchQuery: '',
	sortBy: DEFAULT_SORT,
	selectedInstructors: [],
	selectedCategories: []
};
const INITIAL_STATE = {
	...DEFAULT_FILTER_STATE,
	availableInstructors: [],
	availableCategories: [],
	filteredData: [],
	pagination: {},
	isLoading: false
};
const CASES = {
	SET_FIELD: 'setField',
	SET_LOADING: 'setLoading',
	SET_DATA: 'setData',
	RESET_FILTERS: 'resetFilters'
};
const FIELDS = {
	SEARCH_QUERY: 'searchQuery',
	SORT_BY: 'sortBy',
	SELECTED_INSTRUCTORS: 'selectedInstructors',
	SELECTED_CATEGORIES: 'selectedCategories'
};

function reactionsReducer(state, action) {
	switch (action.type) {
		case CASES.SET_FIELD:
			return { ...state, [action.field]: action.value };

		case CASES.SET_LOADING:
			return { ...state, isLoading: action.value };

		case CASES.SET_DATA:
			return {
				...state,
				...action.payload
			};

		case CASES.RESET_FILTERS:
			return { ...state, ...DEFAULT_FILTER_STATE };

		default:
			return state;
	}
}

export function useReactions({ existingLimit }) {
	const [state, dispatch] = useReducer(reactionsReducer, INITIAL_STATE);

	const fetchData = useCallback(
		async (page = 1, limit = existingLimit) => {
			dispatch({ type: CASES.SET_LOADING, value: true });

			try {
				const params = {
					page,
					limit,
					search: state.searchQuery,
					sort: state.sortBy,
					instructors: state.selectedInstructors.join(','),
					categories: state.selectedCategories.join(',')
				};

				const { data } = await getReactionApi(params);
				const videos = data.videos || [];
				const pagination = data.pagination || {};

				dispatch({
					type: CASES.SET_DATA,
					payload: { filteredData: videos, pagination }
				});
			} catch {
				Toast.error('Error fetching reaction data');
			} finally {
				dispatch({ type: CASES.SET_LOADING, value: false });
			}
		},
		[state.searchQuery, state.sortBy, state.selectedInstructors, state.selectedCategories, existingLimit]
	);

	const fetchInitialData = async () => {
		try {
			const [cats, instructors] = await Promise.all([getAllMainCategoriesApi(), getAllInstructorsApi()]);
			dispatch({
				type: CASES.SET_DATA,
				payload: {
					availableCategories: cats,
					availableInstructors: instructors.data
				}
			});
			fetchData(1);
		} catch {
			Toast.error('Error fetching initial data');
		}
	};

	useEffect(() => {
		fetchInitialData();
	}, [fetchData]);

	const isDefaultFilters =
		state.searchQuery === DEFAULT_FILTER_STATE.searchQuery &&
		state.sortBy === DEFAULT_FILTER_STATE.sortBy &&
		state.selectedInstructors.length === 0 &&
		state.selectedCategories.length === 0;

	const isResetDisabled = state.isLoading || isDefaultFilters;

	const handleSearchQuery = val => dispatch({ type: CASES.SET_FIELD, field: FIELDS.SEARCH_QUERY, value: val });

	const handleSortChange = newSort => {
		dispatch({ type: CASES.SET_FIELD, field: FIELDS.SORT_BY, value: newSort });
	};

	const handleSelectedInstructors = val =>
		dispatch({ type: CASES.SET_FIELD, field: FIELDS.SELECTED_INSTRUCTORS, value: val });
	const handleSelectedCategories = val =>
		dispatch({ type: CASES.SET_FIELD, field: FIELDS.SELECTED_CATEGORIES, value: val });

	const handlePageChange = newPage => fetchData(newPage);

	const handleLimitChange = newLimit => fetchData(1, newLimit);

	const handleReset = () => dispatch({ type: CASES.RESET_FILTERS });

	return {
		...state,
		handleSearchQuery,
		handleSortChange,
		handleSelectedInstructors,
		handleSelectedCategories,
		handlePageChange,
		handleLimitChange,
		handleReset,
		isResetDisabled
	};
}
